import { Component, Input, OnDestroy, OnInit, Type } from '@angular/core';
import {
  EOtpremnicaTableDto,
  EOtpremniceClient,
  GetEOtpremnicaTableQuery,
  IEOtpremnicaTableDto, IRacunTableDto,
  PaginatedResultOfEOtpremnicaTableDto,
  StatusEOtpremnice,
  TipIzmenePosiljke,
  TipRacuna,
  VrstaDokumentaOtpremnica,
  VrstaJoba,
  VrstaRacuna,
} from '@kodit/core/data-api';
import {
  AlertService,
  ConfigService,
  DocumentPreviewService,
  ensureFileUrl,
  LocationService,
} from '@kodit/core/services';
import {
  ActionMode,
  ActionType,
  DynamicDialogConfig,
  DynamicDialogService,
  OnTableInit,
  SelectMode,
  TableConfig,
} from '@kodit/core/shared-ui';
import { Store } from '@ngrx/store';
import { setFilter } from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.actions';
import { selectFilterByKey } from 'libs/core/shared-subforms/src/lib/racun/state/filters/filter.selectors';
import { DialogSize } from 'libs/core/shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import { ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PosaljiNaSefOtpremnicaFormComponent } from '../subforms/posalji-na-sef-otpremnica-form/posalji-na-sef-otpremnica-form.component';
import { StornoEotpremnicaFormComponent } from '../subforms/storno-eotpremnica-form/storno-eotpremnica-form.component';
import { StartVoznjeFormComponent } from '../subforms/start-voznje-form/start-voznje-form.component';
import { EotpremnicaFormService } from '../otpremnice/eotpremnica-form.service';
import { KreiranjePrijemniceFormComponent } from '../prijemnice/kreiranje-prijemnice-form/kreiranje-prijemnice-form.component';
import { SinhronizacijaEotpremniceFormComponent } from '../subforms/sinhronizacija-eotpremnice-form/sinhronizacija-eotpremnice-form.component';
import { PromenaVozilaFormComponent } from '../subforms/promena-vozila-form/promena-vozila-form.component';
import { ProgressService } from 'libs/core/core-ui/src/lib/progress.service';

@Component({
  selector: 'kodit-eotpremnica-table',
  templateUrl: './eotpremnica-table.component.html',
  styleUrls: ['./eotpremnica-table.component.scss']
})
export class EotpremnicaTableComponent implements OnInit, OnDestroy, OnTableInit {

  /** Subs */
  private _subs: Subscription = new Subscription();

  /** Configurations */
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(DialogSize.SMALL);

  /** Props */
  paginatedData!: PaginatedResultOfEOtpremnicaTableDto;
  filters!: GetEOtpremnicaTableQuery;
  private _skipFirst = 0;
  numberOfRowsDisplayed = 0;
  private _isBusy = false;

  isFirstLoad = true;
  isLoading = true;

  otpremniceTable: IEOtpremnicaTableDto[] = [];

  /** Configurations */
  tableConfig!: TableConfig;

  /** I/O */
  @Input() vrstaOtpremnice: VrstaDokumentaOtpremnica;

  constructor(
    private _locationService: LocationService,
    private _storage: Store,
    private _configService: ConfigService,
    private _client: EOtpremniceClient,
    private _alertService: AlertService,
    private _confirmationService: ConfirmationService,
    private _dialogService: DynamicDialogService,
    private _fs: EotpremnicaFormService,
    private _docPreviewService: DocumentPreviewService,
    private _progressService: ProgressService
  ) { }

  ngOnInit(): void {

    this.setTableConfig().then();

    this._subs.add(
      this._progressService
        .getJobZavrsen(VrstaJoba.SINHRONIZACIJA_ULAZNIH_EOTPREMNICA)
        .subscribe(() => {
          this._load();
        })
    );

    // reload tabele kada se filter promeni
    this._subs.add(
      this._storage
        .select(selectFilterByKey('EOtpremnice'))
        .subscribe((data) => {
          if (data) {
            this.filters = JSON.parse(data);
            this._skipFirst = this.filters.pageNumber ?? 0;
            this.numberOfRowsDisplayed = this.filters.pageSize ?? 0;
          } else {
            this.filters = new GetEOtpremnicaTableQuery({
              pageNumber: this._skipFirst,
              pageSize: this.numberOfRowsDisplayed,
            });
          }
          this._load();
        })
    );
  }

   async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      isLazy: true,
      lazyCallbackFunction: (event) => {
        if (!this.isFirstLoad) {
          this._skipFirst = event.first!;
          this.numberOfRowsDisplayed = event.rows!;
        }
        this.updateFilterDataAndReload();
        if (this.isFirstLoad) {
          this.isFirstLoad = false;
        }
      },
      selectMode: SelectMode.SINGLE,
      tableHeader: 'Otpremnice',
      //onChipRemove: (chips: FilterChipDto) => this._handleChipRemove(chips),
      columns: [
        {
          field: 'brojOtpremnice',
          header: 'Broj',
          type: 'link',
          linkCallbackFunction: (_itemIndex: number) => {},
          columns: [
            {
              field: 'vezaFakture',
              subField: 'label',
              header: 'Veza',
              type: 'link',
              linkCallbackFunction: (itemIndex: number, _subIndex: number) =>
                this._goToRacunInfo(itemIndex),
            },
          ],
        },
        {
          field: 'stranka',
          header: 'Primalac robe',
          type: 'text',
        },
        {
          field: 'adresaOtpreme',
          header: 'Adresa otpreme',
          type: 'text',
        },
        {
          field: 'adresaOdredista',
          header: 'Adresa odredišta',
          type: 'text',
        },
        {
          field: 'datumIzdavanja',
          header: 'Datum izdavanja',
          type: 'text',
        },
        {
          field: 'planiraniDatumOtpremeString',
          header: 'Planirani datum otpreme',
          type: 'text',
        },
        {
          field: 'stvarniDatumOtpremeString',
          header: 'Stvarni datum otpreme',
          type: 'text',
        },
        {
          field: 'statusDto',
          subField: 'statusStr',
          header: 'Status',
          styleClass: 'pdv-status',
          styleClassField: 'statusBadgeStr',
          tooltipField: 'opis',
          type: 'badge',
        },
      ],
      headerActions: [
        {
          type: ActionType.CREATE,
          //tipPristupa: TipPristupa.PDV_CRUD,
          hasAccessTooltip: 'Unesite pdv evidenciju',
          noAccessTooltip: 'Nemate ovlašćenja za kreiranje otrpemnice',
          callback: () => this._routeToCreate(),
          isVisible: this._shouldDisplayUnos()
        },
        {
          type: ActionType.CUSTOM,
          //tipPristupa: TipPristupa.PDV_CRUD,
          label: 'Sinhronizuj',
          icon: 'fa-light fa-rotate',
          hasAccessTooltip: 'Sinhronizuj sa sefom',
          noAccessTooltip: 'Nemate ovlašćenja za sinhronizaciju sa sefom',
          callback: () => {
            this.dialogConfig.header = `Sinhronizacija sa SEF-om`;

            this.dialogConfig.customSubmitButton = {
              icon: 'fas fa-paper-plane',
              label: 'Sinhronizuj',
              class: 'mw-90',
            };
            this.openDialog(SinhronizacijaEotpremniceFormComponent);
          },
        },
      ],
      rowActions: [
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          icon: 'fa-regular fa-message-exclamation',
          hasAccessTooltip: 'Pogledajte razlog storniranja',
          callback: (index: number) => {
            this._confirmationService.confirm({
              header: 'Razloga storniranja',
              message: this._getPaginatedItem(index).razlogStorniranja,
              acceptVisible: false,
              rejectLabel: 'Zatvori',
              rejectButtonStyleClass: 'p-button-outlined'
            });
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.STORNIRANO
            );
          },
        },
        {
          type: ActionType.EDIT,
          hasAccessTooltip: 'Izmenite eOtpremnicu',
          noAccessTooltip: 'Nemate ovlašćenja za izmenu eOtpremnice',
          callback: (index: number) => {
            this._routeToEdit(index);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.NACRT &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.IZLAZNA
            );
          },
        },
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fas fa-paper-plane',
          label: 'Pošalji na SEF',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Pošaljite otpremnicu na SEF',
          noAccessTooltip: 'Nemate ovlašćenje za slanje otpremnice na SEF',
          callback: (index: number) => {
            this.dialogConfig.header = `Slanje na SEF - otpremnica ${
              this._getPaginatedItem(index).brojOtpremnice
            }`;

            this.dialogConfig.customSubmitButton = {
              icon: 'fas fa-paper-plane',
              label: 'Pošalji',
              class: 'mw-90',
            };
            this.dialogConfig.data = {
              otpremnicaId: this._getPaginatedItem(index).id,
              planiraniDatumOtpreme: this._getPaginatedItem(index).planiraniDatumOtpreme,
              stvarniDatumOtpreme: this._getPaginatedItem(index).stvarniDatumOtpreme,
            };
            this.openDialog(PosaljiNaSefOtpremnicaFormComponent);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.NACRT &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.IZLAZNA
            );
          },
        },
        {
          mode: ActionMode.SINGLE,
          type: ActionType.CUSTOM,
          icon: 'fas fa-print',
          hasAccessTooltip: 'Odštampajte otpremnicu u PDF formatu',
          noAccessTooltip: 'Nemate ovlašćenja za štampanje otpremnice',
          callback: (index: number) => {
            this._openDocumentPreview(
              this._getPaginatedItem(index).id,
              this._getPaginatedItem(index).brojOtpremnice
            );
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
             return (
              rowData.statusDto?.status !== StatusEOtpremnice.NACRT
              &&  rowData.statusDto?.status !== StatusEOtpremnice.SLANJE
            );
          },
        },
        /* Storno otpremnice */
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fa-solid fa-s',
          label: 'Storniraj otpremnicu',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Storniraj otpremnicu',
          noAccessTooltip: 'Nemate ovlašćenje za storniranje otpremnice',
          callback: (index: number) => {
            this.dialogConfig.header = `Storniranje otpremnice ${
              this._getPaginatedItem(index).brojOtpremnice
            }`;
            this.dialogConfig.customSubmitButton = {
              icon: 'fa-solid fa-s',
              label: 'Storniraj',
              class: 'mw-90',
            };
            this.dialogConfig.data = {
              otpremnicaId: this._getPaginatedItem(index).id,
            };
            this.openDialog(StornoEotpremnicaFormComponent);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.POSLATO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.IZLAZNA
            );
          },
        },
        /* Start voznje */
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fa-regular fa-truck-arrow-right',
          label: 'Početak voznje',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Početak voznje',
          noAccessTooltip: 'Nemate ovlašćenje za pocetak voznje',
          callback: (index: number) => {
            this.dialogConfig.header = `Početak voznje za ${
              this._getPaginatedItem(index).brojOtpremnice
            }`;

            this.dialogConfig.customSubmitButton = {
              icon: 'fa-regular fa-truck-arrow-right',
              label: 'Potvrdi',
              class: 'mw-90',
            };
            this.dialogConfig.data = {
              otpremnicaId: this._getPaginatedItem(index).id,
            };
            this.openDialog(StartVoznjeFormComponent);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.POSLATO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.IZLAZNA &&
              rowData.pocetakVoznje == false
            );
          },
        },
        /* Pretovar */
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fa-solid fa-truck-ramp-box',
          label: 'Pretovar',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Neplanirani pretovar',
          noAccessTooltip: 'Nemate ovlašćenje za unos pretovara',
          callback: (index: number) => {
            this._fs.setJePretovarEdit(true);
            this._routeToEdit(index);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.POSLATO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.IZLAZNA &&
              rowData.imaFazeVoznje
            );
          },
        },
        /* Promena vozila */
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fa-solid fa-truck-ramp-box',
          label: 'Promena vozila',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Promena vozila',
          noAccessTooltip: 'Nemate ovlašćenje za promenu vozila',
          callback: (index: number) => {
            this.dialogConfig.header = `Promena vozila za ${
            this._getPaginatedItem(index).brojOtpremnice }`;

            this.dialogConfig.customSubmitButton = {
              icon: 'fa-regular fa-truck-arrow-right',
              label: 'Izmeni',
              class: 'mw-90',
            };
            this.dialogConfig.data = {
              otpremnicaId: this._getPaginatedItem(index).id,
            };
            this.openDialog(PromenaVozilaFormComponent);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.POSLATO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.IZLAZNA &&
              rowData.imaFazeVoznje && !rowData.pocetakVoznje
            );
          },
        },
        /* Fizicki prijem */
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fa-solid fa-check',
          label: 'Fizički prijem',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Fizički prijem',
          noAccessTooltip: 'Nemate ovlašćenje za fizički prijem',
          callback: (index: number) => {
            this._subs.add(
              this._client
                .akcijaOtpremnice(
                  this._getPaginatedItem(index).id!,
                  TipIzmenePosiljke.FIZICKI_PRIJEM_ROBE
                )
                .subscribe((res) => {
                  if (res.succeeded) {
                    this._alertService.addSuccessMsg("Uspešna akcija fizičkog prijema");
                    this._load();
                    return;
                  }
                  this._alertService.addFailedMsg(res.messages[0]);
                })
            );
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.PRIMLJENO &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.ULAZNA
            );
          },
        },
        {
          mode: ActionMode.MULTI,
          type: ActionType.CUSTOM,
          icon: 'fas fa-plus',
          label: 'Kreiraj prijemnicu',
          shouldDisableWhenSefInactive: true,
          hasAccessTooltip: 'Kreiraj prijemnicu',
          noAccessTooltip: 'Nemate ovlašćenje za kreiranje prijemnice',
          callback: (index: number) => {
            this.dialogConfig.header = `Kreiranje prijemnice za izlaznu ${
              this._getPaginatedItem(index).brojOtpremnice
            } otpremnicu`;

            this.dialogConfig.customSubmitButton = {
              icon: 'fas fa-plus',
              label: 'Kreiraj',
              class: 'mw-90',
            };
            this.dialogConfig.data = {
              id: this._getPaginatedItem(index).id,
            };
            this.dialogConfig.dialogSize = DialogSize.LARGE;
            this.dialogConfig.maximized = true;
            this.openDialog(KreiranjePrijemniceFormComponent);
          },
          shouldDisplayByCondition: (rowData: EOtpremnicaTableDto) => {
            return (
              rowData.statusDto?.status == StatusEOtpremnice.FIZICKI_PRIJEM &&
              rowData.vrstaDokumenta == VrstaDokumentaOtpremnica.ULAZNA
            );
          },
        },
      ],
      // advancedFilter: {
      //   component: PdvEvidencijaPojedinacna39FilterComponent,
      //   data: new GetPdvEvidencijaPojedinacna39TableQuery(),
      // },
    });

  }

  private _load() {
    if (this._isBusy) {
      return;
    }

    this._configService.setIsBusy = true;
    this._isBusy = true;

    if (!this.filters) {
      this.filters = new GetEOtpremnicaTableQuery({
        pageNumber: this._skipFirst,
        pageSize: this.numberOfRowsDisplayed,
      });
    }
    this.filters.vrstaDokumenta = this.vrstaOtpremnice;

    this.isLoading = true;
    this._subs.add(
      this._client
        .getForTableEOtpremnica(this.filters as GetEOtpremnicaTableQuery)
        .pipe(
          finalize(() => {
            this._configService.setIsBusy = false;
            this._isBusy = false;
            this.isLoading = false;
          })
        )
        .subscribe(
          (res) => {
            this.paginatedData = res;
            this.otpremniceTable = res.data!;
            this.otpremniceTable?.forEach((item) => {
              (item as any).vezaFakture = item.racunBroj
                ? [{ label: item.racunBroj }]
                : [];
            });
            //this.chipItems = res.activeFilters!;
            if (this.tableConfig.advancedFilter) {
              this.tableConfig.advancedFilter.data = this.filters;
            }
          },
          (error) => {
            this._configService.setIsBusy = false;
          }
        )
    );
  }

  private updateFilterDataAndReload() {
    if (
      (this._skipFirst !== this.filters.pageNumber ||
        this.numberOfRowsDisplayed !== this.filters.pageSize) &&
      !this.isFirstLoad
    ) {
      this.filters.pageNumber = this._skipFirst;
      this.filters.pageSize = this.numberOfRowsDisplayed;
      this._updateStorage();
    }
  }

  private _updateStorage() {
    this._storage.dispatch(
      setFilter({
        key: 'EOtpremnice',
        filter: JSON.stringify(this.filters),
      })
    );
  }

  private _routeToCreate() {
    this._locationService.routeToEOtpremnicaCrete().then();
  }

  private _routeToEdit(rowIndex: number) {
    this._locationService
      .routeToEOtpremnicaUpdate(
        this._getPaginatedItem(rowIndex).id!
      )
      .then();
  }

  private _goToRacunInfo(rowIndex: number) {
    const item = this._getPaginatedItem(rowIndex);
    if (!item.racunId || !item.racunTip) {
      return;
    }
    let vrsta: VrstaRacuna;
    if (item.racunTip === TipRacuna.IZLAZNA_FAKTURA) {
      vrsta = VrstaRacuna.IZLAZNI;
    } else if (item.racunTip === TipRacuna.ULAZNA_FAKTURA) {
      vrsta = VrstaRacuna.ULAZNI;
    } else {
      vrsta = item.racunVrsta;
    }
    if (!vrsta) {
      return;
    }
    this._locationService
      .routeToRacunKartica(item.racunId, item.racunTip, vrsta)
      .then();
  }

  private _getPaginatedIndex(index: number): number {
    return index - this._skipFirst;
  }

  private _getPaginatedItem(index: number): IEOtpremnicaTableDto {
    return this.otpremniceTable[this._getPaginatedIndex(index)];
  }

  private _openDocumentPreview(
    otpremnicaId: number,
    fileName: string
  ) {
    this._subs.add(
      this._docPreviewService
        .openDialog(
          `eotpremnice/get-pdf/${otpremnicaId}/${ensureFileUrl(fileName)}`
        )
        .onClose.subscribe((res: any) => {
          // just reset dialog to default
          this.dialogConfig.maximisable = true;
          this.dialogConfig.maximized = false;
          this.dialogConfig.hideFooter = false;
        })
    );
  }

  openDialog(
    formType: Type<PosaljiNaSefOtpremnicaFormComponent>
      | Type<StornoEotpremnicaFormComponent>
      | Type<StartVoznjeFormComponent>
      | Type<KreiranjePrijemniceFormComponent>
      | Type<SinhronizacijaEotpremniceFormComponent>
      | Type<PromenaVozilaFormComponent>
  ): void {
    const ref = this._dialogService.open(formType, this.dialogConfig);

    this._subs.add(
      ref.onClose.subscribe((dto: any) => {
        if (dto) {
          this._load();
        }
        this.dialogConfig.hideFooter = false;
      })
    );
  }

  private _shouldDisplayUnos(): boolean {
    return this.vrstaOtpremnice === VrstaDokumentaOtpremnica.IZLAZNA;
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }
}
